import { TActionPlan, TActionPlanTask } from '@valuecase/common'
import { AnimatePresence, motion } from 'framer-motion'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import { IonIcon } from '../../../Icons/IonIcon'
import { Button2 } from '../../../components/ui'
import { CheckMark } from '../../../components/ui/checkmark/CheckMark'
import { Skeleton } from '../../../components/ui/skeleton'
import { cn } from '../../../lib'
import { useDayDifferenceCalculator } from '../../../utils'
import { extractTitleFromQuillObject } from '../../../utils/extractTitleFromQuillObject'
import { NoTasksExistEmptyState } from '../../action-plans'
import ActionPlanCircularProgress from '../components/ActionPlanCircularProgress'
import { useSpaceSidebar } from '../context/SpaceSidebarDataContext'

const TasksTab = () => {
  const {
    actionPlans: { data: actionPlans, isLoading },
  } = useSpaceSidebar()

  if (isLoading) {
    return (
      <div className='p-5 h-full overflow-y-auto'>
        <div className='flex flex-col gap-3'>
          {[...Array(3)].map((_, index) => (
            <div key={index} className='bg-grey-s1 border border-grey-s2 rounded-md'>
              <div className='p-3'>
                <div className='flex items-center gap-1'>
                  <Skeleton className='w-4 h-4' />
                  <Skeleton className='w-8 h-8 rounded-full' />
                  <div className='flex flex-col flex-1 gap-2'>
                    <Skeleton className='h-4 w-24' />
                    <Skeleton className='h-4 w-48' />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!isLoading && actionPlans.length === 0) {
    return <NoTasksExistEmptyState />
  }

  return (
    <div className='p-5 h-full overflow-y-auto relative group'>
      <div className='flex flex-col gap-3'>
        {actionPlans.map((actionPlan, index) => (
          <ActionPlanAccordion key={actionPlan.id} actionPlan={actionPlan} isFirst={index === 0} />
        ))}
      </div>
    </div>
  )
}

const ActionPlanAccordion = ({
  actionPlan,
  isFirst,
}: {
  actionPlan: TActionPlan
  isFirst: boolean
}) => {
  const [isExpanded, setIsExpanded] = useState(isFirst)
  const [expandedMilestones, setExpandedMilestones] = useState<Record<string, boolean>>(() => {
    if (isFirst) {
      return actionPlan.milestones.reduce(
        (acc, milestone) => {
          acc[milestone.id] = true
          return acc
        },
        {} as Record<string, boolean>,
      )
    }
    return {}
  })
  const [isHovered, setIsHovered] = useState(false)
  const { onOpenBlock, space } = useSpaceSidebar()
  const title = useMemo(() => {
    return extractTitleFromQuillObject(actionPlan.title) ?? 'Action plan'
  }, [actionPlan.title])

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev)
    // When expanding, set all milestones to expanded
    if (!isExpanded) {
      const allMilestonesExpanded = actionPlan.milestones.reduce(
        (acc, milestone) => {
          acc[milestone.id] = true
          return acc
        },
        {} as Record<string, boolean>,
      )
      setExpandedMilestones(allMilestonesExpanded)
    }
  }, [actionPlan.milestones, isExpanded])

  const toggleMilestone = useCallback((milestoneId: string) => {
    setExpandedMilestones((prev) => ({
      ...prev,
      [milestoneId]: !prev[milestoneId],
    }))
  }, [])

  return (
    <div
      className={cn('bg-grey-s1 relative rounded-md', {
        shadow: isExpanded,
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className='absolute top-1 right-1 opacity-0 transition-opacity'
        style={{ opacity: isHovered ? 1 : 0 }}
      >
        <Button2
          variant='subtle'
          size='small'
          leadingIcon='open-outline'
          onClick={() => {
            if (!space?.rootNodeId) return
            onOpenBlock({
              spaceId: space?.rootNodeId,
              blockId: actionPlan.nodeId,
            })
          }}
        />
      </div>
      <div
        className={cn('p-3 cursor-pointer  rounded-t-md bg-white ', {
          'rounded-b-md': !isExpanded,
        })}
        onClick={toggleExpand}
      >
        <div className='flex items-center gap-1 '>
          <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.1 }}
            className='flex items-center'
          >
            <Button2
              leadingIcon='chevron-forward'
              className='text-grey-s5'
              variant='ghost'
              size='x-small'
              onClick={(e) => {
                e.stopPropagation()
                toggleExpand()
              }}
            />
          </motion.div>
          <div className='flex items-center w-8 h-8 justify-center'>
            <ActionPlanCircularProgress actionPlan={actionPlan} size={8} />
          </div>
          <div className='flex flex-col flex-1 min-w-0'>
            <div className='text-sm text-grey-s5'>Action plan</div>
            <div className='font-semibold text-sm text-grey-s6 truncate break-words max-w-[32ch]'>
              {title}
            </div>
          </div>
        </div>
      </div>

      {/* Milestones Section */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.1, ease: 'easeInOut' }}
            className='overflow-hidden  rounded-b-md py-2 bg-grey-s1 flex flex-col gap-2'
          >
            {actionPlan.milestones.map((milestone) => (
              <MilestoneItem
                key={milestone.id}
                milestone={milestone}
                isExpanded={!!expandedMilestones[milestone.id]}
                toggleExpand={() => toggleMilestone(milestone.id)}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface MilestoneItemProps {
  milestone: {
    id: string
    title: string
    tasks: Array<TActionPlanTask>
  }
  isExpanded: boolean
  toggleExpand: () => void
}

const MilestoneItem = ({ milestone, isExpanded, toggleExpand }: MilestoneItemProps) => {
  return (
    <div className='bg-grey-s1 flex flex-col'>
      <div
        className='flex items-center justify-between px-2 pt-2 cursor-pointer '
        onClick={toggleExpand}
      >
        <div className='flex items-center gap-2'>
          <span className='font-semibold text-sm text-grey-s6'>{milestone.title}</span>
        </div>
        <motion.div
          initial={{ rotate: 0 }}
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.1 }}
        >
          <Button2
            leadingIcon='chevron-down'
            variant='ghost'
            size='x-small'
            onClick={(e) => {
              e.stopPropagation()
              toggleExpand()
            }}
          />
        </motion.div>
      </div>

      {/* Tasks Section */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.1, ease: 'easeInOut' }}
            className='overflow-hidden'
          >
            <div className='px-2 py-2 space-y-2'>
              {milestone.tasks.map((task) => (
                <TaskItem key={task.id} task={task} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const TaskItem = ({ task }: { task: TActionPlanTask }) => {
  const { onCheckTask } = useSpaceSidebar()

  const dayDifferenceCalculator = useDayDifferenceCalculator({
    // Force english language for seller dashboard - other languages only supported for spaces frontend
    language: 'en-GB',
  })
  const timeToDueDate = useMemo(() => {
    if (!task.dueDate) {
      return null
    }
    const result = dayDifferenceCalculator.daysDiffFromNowOrFormatted({
      to: DateTime.fromJSDate(task.dueDate, {
        zone: 'utc',
      }),
    })
    return result.message
  }, [dayDifferenceCalculator, task.dueDate])

  const isPastDue = useMemo(() => {
    if (!task.dueDate) return false
    return DateTime.fromJSDate(task.dueDate, { zone: 'utc' }) < DateTime.utc()
  }, [task.dueDate])

  const description = useMemo(() => {
    return extractTitleFromQuillObject(task.description ?? '')
  }, [task.description])

  return (
    <div
      className={cn('bg-white p-2 rounded-md', {
        'opacity-85': task.checked,
      })}
    >
      <div className='flex items-start gap-3'>
        <div className='flex items-center h-6'>
          <CheckMark
            className='cursor-pointer'
            checked={task.checked}
            onClick={() => onCheckTask(task.id, !task.checked)}
          />
        </div>
        <div className='flex-1 truncate max-w-[32ch]'>
          <span
            className={cn('text-sm font-medium text-grey-s6 leading-6', {
              'line-through text-grey-s4': task.checked,
            })}
          >
            {task.title}
          </span>
          {task.description && (
            <>
              <br />
              <span
                className={cn('text-xs', {
                  'text-grey-s4': task.checked,
                  'text-grey-s5': !task.checked,
                })}
              >
                {description}
              </span>
            </>
          )}
        </div>
      </div>
      <div className='flex items-center flex-end mt-2'>
        {task.completedAt && task.dueDate && (
          <div className='flex items-center ml-auto'>
            <IonIcon name={'c_calendar-clear-checked'} className={'w-4 h-4 px-1 text-grey-s4'} />
          </div>
        )}
        {task.dueDate && !task.completedAt && (
          <div className='flex items-center gap-2 ml-auto'>
            <IonIcon
              name='calendar'
              className={cn('text-xs', {
                'text-warning-s5': isPastDue,
                'text-grey-s5': !isPastDue,
              })}
            />
            <div
              className={cn('text-xs', {
                'text-warning-s5': isPastDue,
                'text-grey-s5': !isPastDue,
              })}
            >
              {timeToDueDate}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TasksTab
